/**
 * Pure helpers: map onboarding viewStep to i18n keys for headline and subtitle.
 * No React; used by onboarding page for PageHeader.
 * viewStep 0=Intro, 1=Who setup, 2=Situation, 3=DGI access (everyone).
 * Non-accountant owner: 4–6 cert path, 7 CAE, optional 8 CFE, then fiscal → literal E → personalize → plan / Ready.
 * Accountant: 4+=invite, progress, … (s = viewStep - 1).
 */
import type { OwnerPostCertViewSteps } from "@/pages/onboarding/ownerPostCertSteps";

function ownerPostCertHeadlineKey(
  viewStep: number,
  isMigrating: boolean | null,
  needsCfeImportStep: boolean,
  st: OwnerPostCertViewSteps
): string | null {
  if (viewStep === st.cae) return "onboarding.caeStepHeadline";
  if (needsCfeImportStep && st.cfe !== null && viewStep === st.cfe) {
    return "onboarding.cfeImportStepHeadline";
  }
  if (viewStep === st.fiscal) return "onboarding.stepFiscalHeadline";
  if (st.literalE !== null && viewStep === st.literalE) {
    return "onboarding.stepLiteralEHeadline";
  }
  if (viewStep === st.personalize) return "onboarding.step2PersonalizeHeadline";
  if (isMigrating && viewStep === st.migrationOrPlanFirst) {
    return "onboarding.stepMigrationHeadline";
  }
  if (
    (!isMigrating && viewStep === st.migrationOrPlanFirst) ||
    (isMigrating && viewStep === st.planMigrating)
  ) {
    return "onboarding.stepPlanHeadline";
  }
  return null;
}

function ownerPostCertSubtitleKey(
  viewStep: number,
  isMigrating: boolean | null,
  needsCfeImportStep: boolean,
  st: OwnerPostCertViewSteps
): string | null {
  if (viewStep === st.cae) return "";
  if (needsCfeImportStep && st.cfe !== null && viewStep === st.cfe) {
    return "";
  }
  if (viewStep === st.fiscal) return "onboarding.stepFiscalSubtitle";
  if (st.literalE !== null && viewStep === st.literalE) {
    return "onboarding.stepLiteralESubtitle";
  }
  if (viewStep === st.personalize) return "onboarding.step2PersonalizeSubtitle";
  if (isMigrating && viewStep === st.migrationOrPlanFirst) {
    return "onboarding.stepMigrationSubtitle";
  }
  if (
    (!isMigrating && viewStep === st.migrationOrPlanFirst) ||
    (isMigrating && viewStep === st.planMigrating)
  ) {
    return "onboarding.stepPlanSubtitle";
  }
  return null;
}

export function getStepHeadlineKey(
  viewStep: number,
  totalSteps: number,
  isMigrating: boolean | null,
  isAccountantFlowOwner?: boolean,
  isAccountantUser?: boolean,
  isAlreadyIssuer?: boolean,
  certificateAcquisitionChoice?: string | null,
  step6FiscaloGoToAbitab?: boolean,
  step6AwaitingAbitab?: boolean,
  ownerPostCertViewSteps?: OwnerPostCertViewSteps | null,
  needsCfeImportStep?: boolean,
  preCertOffset = 0
): string {
  // Self path: situation and later steps shift by preCertOffset (régimen + plan
  // precede situation). Those pre-plan steps are shell-adopted so this header is
  // hidden for them; the shifted checks below cover the steps that still show it.
  const off = !isAccountantFlowOwner && !isAccountantUser ? preCertOffset : 0;
  const isLastStep = viewStep === totalSteps;
  if (viewStep === 0 || viewStep === 1) return "onboarding.introHeadline";
  if (viewStep === 2 + off) return "onboarding.situationHeadline";
  if (viewStep === 3 + off) return "onboarding.dgiAccessHeadline";
  if (
    viewStep === 4 + off &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    return "onboarding.certificateAcquisitionHeadline";
  }
  if (
    viewStep === 5 + off &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    return "onboarding.certificateAcquisitionStepsHeadline";
  }
  if (
    viewStep === 6 + off &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    if (certificateAcquisitionChoice === "fiscalo") {
      if (step6AwaitingAbitab) return "onboarding.step6AwaitingAbitabHeadline";
      if (step6FiscaloGoToAbitab) return "onboarding.step6FiscaloGoToAbitabHeadline";
      return "onboarding.step6FiscaloHeadline";
    }
    if (isAlreadyIssuer) return "onboarding.step3CertHeadlineAlreadyIssuer";
    return "onboarding.step3CertHeadline";
  }
  if (isLastStep) return "onboarding.stepReadyHeadline";
  if (
    !isAccountantFlowOwner &&
    !isAccountantUser &&
    ownerPostCertViewSteps
  ) {
    const key = ownerPostCertHeadlineKey(
      viewStep,
      isMigrating,
      Boolean(needsCfeImportStep),
      ownerPostCertViewSteps
    );
    if (key !== null) return key;
  }
  const s =
    isAccountantFlowOwner || isAccountantUser
      ? viewStep >= 4
        ? viewStep - 1
        : viewStep
      : viewStep >= 7
        ? viewStep >= 10
          ? viewStep - 1
          : viewStep - 3
        : viewStep;
  if (isAccountantFlowOwner && s === 3) return "onboarding.inviteStepHeadline";
  if (isAccountantFlowOwner && s === 4) return "onboarding.progressStepHeadline";
  if (isAccountantFlowOwner && s === 5) return "onboarding.step2PersonalizeHeadline";
  if (isAccountantFlowOwner && s === 6) return "onboarding.stepPlanHeadline";
  if (isAccountantFlowOwner && s === 7) return "onboarding.ownerSuccessHeadline";
  if (isAccountantUser && s === 6) return "onboarding.step3CertHeadline";
  if (isAccountantUser && s === 7) return "onboarding.accountantSuccessHeadline";
  if (s === 3) return "onboarding.step1Headline";
  if (s === 4) return "onboarding.stepFiscalHeadline";
  if (s === 5) return "onboarding.stepLiteralEHeadline";
  if (s === 6) return "onboarding.step2PersonalizeHeadline";
  if (s === 9 && isMigrating) return "onboarding.stepMigrationHeadline";
  if ((s === 9 && !isMigrating) || (s === 10 && isMigrating))
    return "onboarding.stepPlanHeadline";
  return "onboarding.stepReadyHeadline";
}

export function getStepSubtitleKey(
  viewStep: number,
  totalSteps: number,
  isMigrating: boolean | null,
  isAccountantFlowOwner?: boolean,
  isAccountantUser?: boolean,
  isAlreadyIssuer?: boolean,
  certificateAcquisitionChoice?: string | null,
  step6FiscaloGoToAbitab?: boolean,
  step6AwaitingAbitab?: boolean,
  ownerPostCertViewSteps?: OwnerPostCertViewSteps | null,
  needsCfeImportStep?: boolean,
  preCertOffset = 0
): string {
  const off = !isAccountantFlowOwner && !isAccountantUser ? preCertOffset : 0;
  const isLastStep = viewStep === totalSteps;
  if (viewStep === 0 || viewStep === 1) return "onboarding.stepWhoSetupHeadline";
  if (viewStep === 2 + off) return "";
  if (viewStep === 3 + off) return "onboarding.dgiAccessSubtitle";
  if (
    viewStep === 4 + off &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    return "onboarding.certificateAcquisitionSubtitle";
  }
  if (
    viewStep === 5 + off &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    return "onboarding.certificateAcquisitionStepsSubtitle";
  }
  if (
    viewStep === 6 + off &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    if (certificateAcquisitionChoice === "fiscalo") {
      if (step6AwaitingAbitab) return "onboarding.step6AwaitingAbitabSubtitle";
      if (step6FiscaloGoToAbitab) return "onboarding.step6FiscaloGoToAbitabSubtitle";
      return "onboarding.step6FiscaloSubtitle";
    }
    if (isAlreadyIssuer) return "onboarding.step3CertSubtitleAlreadyIssuer";
    return "onboarding.step3CertSubtitle";
  }
  if (isLastStep) return "onboarding.stepReadySubtitle";
  if (
    !isAccountantFlowOwner &&
    !isAccountantUser &&
    ownerPostCertViewSteps
  ) {
    const key = ownerPostCertSubtitleKey(
      viewStep,
      isMigrating,
      Boolean(needsCfeImportStep),
      ownerPostCertViewSteps
    );
    if (key !== null) return key;
  }
  const s =
    isAccountantFlowOwner || isAccountantUser
      ? viewStep >= 4
        ? viewStep - 1
        : viewStep
      : viewStep >= 7
        ? viewStep >= 10
          ? viewStep - 1
          : viewStep - 3
        : viewStep;
  if (isAccountantFlowOwner && s === 3) return "onboarding.inviteStepSubtitle";
  if (isAccountantFlowOwner && s === 4) return "onboarding.progressStepSubtitle";
  if (isAccountantFlowOwner && s === 5) return "onboarding.step2PersonalizeSubtitle";
  if (isAccountantFlowOwner && s === 6) return "onboarding.stepPlanSubtitle";
  if (isAccountantFlowOwner && s === 7) return "onboarding.ownerSuccessSubtitle";
  if (isAccountantUser && s === 6) return "onboarding.step3CertSubtitle";
  if (isAccountantUser && s === 7) return "onboarding.accountantSuccessSubtitle";
  if (s === 3) return "";
  if (s === 4) return "onboarding.stepFiscalSubtitle";
  if (s === 5) return "onboarding.stepLiteralESubtitle";
  if (s === 6) return "onboarding.step2PersonalizeSubtitle";
  if (s === 9 && isMigrating) return "onboarding.stepMigrationSubtitle";
  if ((s === 9 && !isMigrating) || (s === 10 && isMigrating))
    return "onboarding.stepPlanSubtitle";
  return "onboarding.stepReadySubtitle";
}
